/**
 * Email lure merge fields — the single source of truth for the supported
 * {{token}} set, mirrored from enforcer EmailConfig rendering. Pure logic so
 * the create-form preview and a backend-parity test share one implementation.
 *
 * Contract (SHARED): supported tokens are first_name, last_name, full_name,
 * email, lure_link. {{lure_link}} = relay_base_url + "/c/" + token. Unknown
 * merge fields render as empty string (never left as literal braces).
 */

export interface MergeFieldSpec {
  token: string;
  /** Key into the preview target the token resolves from. */
  key: keyof MergePreviewTarget;
  /** Short operator-facing description shown in the helper. */
  description: string;
}

export interface MergePreviewTarget {
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  lure_link?: string;
}

export const MERGE_FIELDS: MergeFieldSpec[] = [
  { token: "{{first_name}}", key: "first_name", description: "Target's first name" },
  { token: "{{last_name}}", key: "last_name", description: "Target's last name" },
  { token: "{{full_name}}", key: "full_name", description: "Target's full name" },
  { token: "{{email}}", key: "email", description: "Target's email address" },
  {
    token: "{{lure_link}}",
    key: "lure_link",
    description: "Per-target relay link (relay base + /c/ + token)",
  },
];

const KEY_BY_NAME: Record<string, keyof MergePreviewTarget> = Object.fromEntries(
  MERGE_FIELDS.map((f) => [f.token.slice(2, -2).trim(), f.key]),
);

// Matches {{ name }} with optional inner whitespace; name is [a-z0-9_].
const MERGE_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/**
 * Render merge fields against a preview target. Unknown fields and
 * missing/undefined values both resolve to "".
 */
export function renderMergeFields(template: string, target: MergePreviewTarget): string {
  return template.replace(MERGE_RE, (_match, name: string) => {
    const key = KEY_BY_NAME[name];
    if (!key) return "";
    const value = target[key];
    return value ?? "";
  });
}

/** lure_link = relay_base_url + "/c/" + token (base trailing slash normalized). */
export function buildLureLink(relayBaseUrl: string, token: string): string {
  const base = relayBaseUrl.replace(/\/+$/, "");
  return `${base}/c/${token}`;
}
